import { useState } from "react";
import { TrendingUp, TrendingDown, Calendar, Landmark } from "lucide-react";
import { motion } from "framer-motion";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useExchangeRates } from "@/hooks/useExchangeRates";
import { describeExcluded } from "@/lib/currencyTotals";
import { useAccountBalances } from "@/hooks/useAccountBalances";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { startOfMonth, startOfWeek, startOfYear, subMonths, isAfter } from "date-fns";
import { useTransactions, useAccounts, useWallets } from "@/hooks/queries";
import { cn } from "@/lib/utils";
import { parseLocalDate } from "@/lib/dates";

type TimePeriod = "this-month" | "last-month" | "this-week" | "this-year" | "all-time";

export const DashboardStats = () => {
  const { formatCurrency } = useUserPreferences();
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("this-month");

  // React Query hooks - data is cached and shared across components
  const { data: transactions = [] } = useTransactions();
  const { data: accounts = [], isLoading: accountsLoading } = useAccounts();
  const { data: wallets = [], isLoading: walletsLoading } = useWallets();

  const loading = accountsLoading || walletsLoading;

  // Get the start date based on selected time period
  const getStartDate = (period: TimePeriod): Date | null => {
    const now = new Date();
    switch (period) {
      case "this-month":
        return startOfMonth(now);
      case "last-month":
        return startOfMonth(subMonths(now, 1));
      case "this-week":
        return startOfWeek(now, { weekStartsOn: 1 });
      case "this-year":
        return startOfYear(now);
      case "all-time":
        return null;
    }
  };

  // Get end date for last-month filter
  const getEndDate = (period: TimePeriod): Date | null => {
    if (period === "last-month") {
      return startOfMonth(new Date());
    }
    return null;
  };

  // Get currencies for exchange rate fetching
  const accountCurrencies = [
    ...accounts.map(a => a.currency),
    ...wallets.map(w => w.currency)
  ].filter(Boolean) as string[];

  const { sumInBase, ready: ratesReady } = useExchangeRates(accountCurrencies);

  // Calculate balances from transactions
  const { getAccountBalance, getWalletBalance } = useAccountBalances(
    transactions,
    accounts.map(a => ({ id: a.id, name: a.name, initial_balance: a.initial_balance || 0, balance_as_of_date: a.balance_as_of_date })),
    wallets.map(w => ({ id: w.id, account_id: w.account_id || '', name: w.name, balance: w.balance || 0 }))
  );

  // Filter to only include accounts not excluded from dashboard
  const visibleAccounts = accounts.filter(a => !a.exclude_from_dashboard);
  const visibleAccountIds = new Set(visibleAccounts.map(a => a.id));
  const visibleWallets = wallets.filter(w => w.account_id && visibleAccountIds.has(w.account_id));

  // Filter out backdated transactions for income/expense totals
  const activeTransactions = transactions.filter(t => !t.is_backdated);

  // Filter transactions by selected time period
  const startDate = getStartDate(timePeriod);
  const endDate = getEndDate(timePeriod);

  const periodTransactions = activeTransactions.filter(t => {
    const txDate = parseLocalDate(t.date);
    if (startDate && !isAfter(txDate, startDate) && txDate.getTime() !== startDate.getTime()) {
      return false;
    }
    if (endDate && isAfter(txDate, endDate)) {
      return false;
    }
    return true;
  });

  // Net worth in the base currency. Balances with no usable rate are left out and
  // named under the total, never added as if they were the base currency (D-056).
  const netWorthSum = sumInBase([
    ...visibleAccounts.map(account => ({
      amount: getAccountBalance({ id: account.id, name: account.name, initial_balance: account.initial_balance || 0, balance_as_of_date: account.balance_as_of_date }),
      currency: account.currency || 'USD',
      manualRate: account.manual_exchange_rate,
    })),
    ...visibleWallets.map(wallet => {
      const parentAccount = accounts.find(a => a.id === wallet.account_id);
      return {
        amount: getWalletBalance(
          { id: wallet.id, account_id: wallet.account_id || '', name: wallet.name, balance: wallet.balance || 0 },
          parentAccount?.name || ""
        ),
        currency: wallet.currency || 'USD',
        manualRate: wallet.manual_exchange_rate,
      };
    }),
  ]);

  const totalIncome = periodTransactions
    .filter((t) => t.type === "income")
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const totalExpenses = periodTransactions
    .filter((t) => t.type === "expense")
    .reduce((sum, t) => sum + Number(t.amount), 0);

  // Calculate trends (comparing current period to previous period)
  const getPreviousPeriodDates = () => {
    const now = new Date();
    switch (timePeriod) {
      case "this-month":
        return {
          start: startOfMonth(subMonths(now, 1)),
          end: startOfMonth(now)
        };
      case "last-month":
        return {
          start: startOfMonth(subMonths(now, 2)),
          end: startOfMonth(subMonths(now, 1))
        };
      case "this-week":
        return {
          start: startOfWeek(subMonths(now, 0), { weekStartsOn: 1 }),
          end: null
        };
      default:
        return { start: null, end: null };
    }
  };

  const prevPeriod = getPreviousPeriodDates();

  const prevPeriodTransactions = prevPeriod.start ? activeTransactions.filter(t => {
    const txDate = parseLocalDate(t.date);
    if (!isAfter(txDate, prevPeriod.start!) && txDate.getTime() !== prevPeriod.start!.getTime()) {
      return false;
    }
    if (prevPeriod.end && isAfter(txDate, prevPeriod.end)) {
      return false;
    }
    return true;
  }) : [];

  const prevIncome = prevPeriodTransactions.filter(t => t.type === "income").reduce((sum, t) => sum + Number(t.amount), 0);
  const prevExpenses = prevPeriodTransactions.filter(t => t.type === "expense").reduce((sum, t) => sum + Number(t.amount), 0);

  const incomeTrend = prevIncome > 0 ? ((totalIncome - prevIncome) / prevIncome) * 100 : undefined;
  const expenseTrend = prevExpenses > 0 ? ((totalExpenses - prevExpenses) / prevExpenses) * 100 : undefined;

  // Net worth = account balances + wallet balances (transactions are already included in these)
  const netWorth = netWorthSum.total;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative overflow-hidden rounded-xl bg-gradient-to-br from-card via-card to-primary/5 border border-border/50 p-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left — Net Worth */}
        <div className="flex flex-col justify-center">
          <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Landmark className="h-4 w-4" />
            Total Net Worth
          </p>
          <p className="text-4xl font-bold tracking-tight font-mono-numbers mt-2">
            {loading || !ratesReady ? "..." : formatCurrency(netWorth)}
          </p>
          {!loading && ratesReady && netWorthSum.excluded.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1" role="status">
              Excludes {describeExcluded(netWorthSum.excluded)} (exchange rate unavailable)
            </p>
          )}
        </div>

        {/* Right — Income + Expenses stacked */}
        <div className="flex flex-col gap-4 md:border-l md:border-border/40 md:pl-6">
          {/* Income */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3 text-income-text" />
                Income
              </p>
              <Select value={timePeriod} onValueChange={(v) => setTimePeriod(v as TimePeriod)}>
                <SelectTrigger className="w-[150px] h-7 text-xs bg-background/50">
                  <Calendar className="h-3 w-3 mr-1.5 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this-month">This Month</SelectItem>
                  <SelectItem value="last-month">Last Month</SelectItem>
                  <SelectItem value="this-week">This Week</SelectItem>
                  <SelectItem value="this-year">This Year</SelectItem>
                  <SelectItem value="all-time">All Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-2xl font-semibold font-mono-numbers text-income-text">
              +{formatCurrency(totalIncome)}
            </p>
            {incomeTrend !== undefined && (
              <p className={cn("text-xs font-medium", incomeTrend >= 0 ? "text-income-text" : "text-expense-text")}>
                {incomeTrend >= 0 ? "+" : ""}{incomeTrend.toFixed(0)}% vs last period
              </p>
            )}
          </div>

          {/* Expenses */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
              <TrendingDown className="h-3 w-3 text-expense-text" />
              Expenses
            </p>
            <p className="text-2xl font-semibold font-mono-numbers text-expense-text">
              -{formatCurrency(totalExpenses)}
            </p>
            {expenseTrend !== undefined && (
              <p className={cn("text-xs font-medium", expenseTrend <= 0 ? "text-income-text" : "text-expense-text")}>
                {expenseTrend >= 0 ? "+" : ""}{expenseTrend.toFixed(0)}% vs last period
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
